/**
 * Configuração visual central — paleta e constantes derivadas da referência
 * (docs/mockup.jpeg). Tudo que define a "cara" do game mora aqui.
 */

import { ACTIVE_CITY } from './world/cities.js';
import { TEX_TIER } from './scene/qualityLevel.js';

export const COLORS = {
  // Terreno / vegetação
  grass: '#1c4429',
  grassDark: '#11301c',
  forest: '#0c2414',

  // Asfalto / vias
  asphalt: '#2f312d',
  asphaltDark: '#222420',
  // Borda escura que separa a quadra da rua (um pouco mais escura que o asfalto)
  blockEdge: '#15160e',
  // Calçadas / praças pavimentadas (cinza-claro quente da referência)
  sidewalk: '#8f9088',
  sidewalkDark: '#74756d',
  curb: '#5a5b54',
  // Piso da superquadra FORA da calçada do prédio (cinza médio: mais escuro que a
  // calçada, mais claro que a rua) — cria hierarquia rua < piso < calçada do prédio
  plazaFloor: '#63645c',
  lane: '#eef4ee',
  laneDim: '#b9c6bd',

  // Architectural greens: mineral walls, lighter roof, deep metal trim.
  // Brand accents retain their own vivid WhatsApp green below.
  buildingRoof: '#65ab7a',
  buildingRoofEdge: '#28654b',
  buildingWall: '#408c62',
  buildingWallDark: '#285940',
  windowOn: '#ffb43c',
  windowOff: '#13502a',

  // Marca / brilhos
  waGreen: '#25d366',
  waGreenBright: '#5dff97',
  glow: '#46ff86',

  // Árvores
  treeA: '#34964f',
  treeB: '#27773d',
  treeC: '#46b86a',
  treeTrunk: '#5b3a22',

  // Céu / fog
  sky: '#06140d',
  fog: '#06160e'
};

/** Mistura dois hex `#rrggbb`: t=0 devolve `a`, t=1 devolve `b`. Usado para derivar
 *  tons a partir de uma cor de marca (ex.: a luz do poste a partir da cor do distrito). */
export function mixHex(a, b, t) {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (sh) => {
    const va = (pa >> sh) & 255;
    const vb = (pb >> sh) & 255;
    return Math.round(va + (vb - va) * t);
  };
  return `#${((1 << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).slice(1)}`;
}

/** Dimensões do mundo (unidades). A cidade vive no plano XZ, Y para cima. */
export const WORLD = {
  width: 330, // eixo X: -165 .. 165 (margem maior → vias de entrada mais longas)
  // eixo Z: -160 .. 160. Profundidade 240→320: DOBRA a extensão das vias de acesso
  // de cima/baixo (cidade termina em z=±80 → extensão 40u→80u). Com a câmera mais
  // aberta, os carros surgiam/desapareciam num ponto VISÍVEL da via superior; agora
  // a ponta (com o degradê p/ a floresta) fica além do enquadramento → animação
  // de entrada/saída uniforme. Vale p/ todas as cidades (spawn/asfalto derivam daqui).
  depth: 320
};

/**
 * Resolução da textura procedural do chão (px por unidade de mundo).
 *
 * 9 é calibração de 1080p: dá 1,10 texel por pixel de tela na vista aberta —
 * praticamente 1:1. Num telão 4K o mesmo número cai para 0,55 (1,8× MAGNIFICADO)
 * e, no fecho da câmera dinâmica, passa de 4×: asfalto, faixas de pedestre,
 * setas e meio-fio viram borrão na maior superfície da tela. O nível Ultra sobe
 * para 14 (canvas 4620×4480), decidido no BOOT — ver TEX_TIER em
 * scene/qualityLevel.js. Fora do browser (harnesses) continua 9.
 */
export const TEXTURE_PX_PER_UNIT = TEX_TIER.groundPx;

/**
 * Malha viária. As vias são as bordas das quadras (grade), com largura fixa.
 * Cada via é APARADA no limite da grade (não avança para a floresta): as
 * verticais vão de z=-74..74 e as horizontais de x=-120..120 — meia-via além
 * das vias de perímetro, só para fechar bem as esquinas. xMax/zMin etc.
 * também abrem espaço ao prédio alto à direita (a via z=-23 não cruza a col).
 *
 * Estes limites valem para a grade 5×3 (a maioria das cidades); a 4×4 tem os seus
 * (G4MIN/G4MAX) — ver GRID_4X4_ROADS mais abaixo.
 */
const GZMIN = -74; // limite vertical da grade (H[0]-meia via)
const GZMAX = 74;
const GXMIN = -120; // limite horizontal da grade (V[0]-meia via)
const GXMAX = 120;

const BASE_ROADS = {
  width: 10,
  vertical: [
    { x: -115, zMin: GZMIN, zMax: GZMAX },
    { x: -69, zMin: GZMIN, zMax: GZMAX },
    { x: -23, zMin: GZMIN, zMax: GZMAX },
    { x: 23, zMin: GZMIN, zMax: GZMAX },
    { x: 69, zMin: GZMIN, zMax: GZMAX },
    { x: 115, zMin: GZMIN, zMax: GZMAX }
  ],
  horizontal: [
    { z: -69, xMin: GXMIN, xMax: GXMAX },
    { z: -23, xMin: GXMIN, xMax: 69 }, // não cruza a coluna do prédio alto (à direita)
    { z: 23, xMin: GXMIN, xMax: GXMAX },
    { z: 69, xMin: GXMIN, xMax: GXMAX }
  ]
};

/**
 * FLIP HORIZONTAL (espelho no eixo X) de uma cidade com `flipX: true` (cities.js):
 * as vias VERTICAIS são simétricas (mantidas como estão — a ordem crescente que o
 * landRect espera), então só as HORIZONTAIS têm os limites em x trocados/negados —
 * a via z=-23 aparada passa da direita p/ a esquerda, acompanhando o prédio alto
 * que também espelha de coluna. Os lots/entradas dessa cidade já vêm espelhados no
 * cities.js; aqui só se espelha a via (compartilhada por todos os módulos de rua).
 */
function flipRoadsX(roads) {
  return {
    width: roads.width,
    vertical: roads.vertical,
    horizontal: roads.horizontal.map((r) => ({ z: r.z, xMin: -r.xMax, xMax: -r.xMin }))
  };
}

/**
 * PRÉDIO ALTO HORIZONTAL (cidade com `tallBlockHoriz: true`, ex.: Conversion): o
 * bloco alto mesclado fica em A3/A4 (linha 0, colunas 2-3) em vez de duas quadras
 * na vertical. Então NENHUMA horizontal é aparada (as 4 vão inteiras) e a VIA
 * VERTICAL x=23 (entre as colunas 2 e 3) é aparada na LINHA 0 (zMin=-23) p/ não
 * cortar o mall. Os lots dessa cidade já vêm com o shopping mesclado em A3/A4.
 */
const TALL_HORIZ_ROADS = {
  width: 10,
  vertical: [
    { x: -115, zMin: GZMIN, zMax: GZMAX },
    { x: -69, zMin: GZMIN, zMax: GZMAX },
    { x: -23, zMin: GZMIN, zMax: GZMAX },
    { x: 23, zMin: -23, zMax: GZMAX }, // aparada na LINHA 0 (não cruza o mall A3/A4)
    { x: 69, zMin: GZMIN, zMax: GZMAX },
    { x: 115, zMin: GZMIN, zMax: GZMAX }
  ],
  horizontal: [
    { z: -69, xMin: GXMIN, xMax: GXMAX },
    { z: -23, xMin: GXMIN, xMax: GXMAX }, // inteira (não há prédio alto na coluna 4)
    { z: 23, xMin: GXMIN, xMax: GXMAX },
    { z: 69, xMin: GXMIN, xMax: GXMAX }
  ]
};

/**
 * GRADE 4×4 (cidade com `cols: 4, rows: 4`, ex.: Mexican Town): 16 quadras em vez
 * das 15 (5×3) das outras. Nos DOIS eixos as vias ficam no MESMO passo de 46u e
 * CENTRADAS em 0 — assim o terreno de cada quadra continua 36×36 e os prédios
 * 18×18, IDÊNTICOS aos das cidades 5×3 (nenhum modelo precisa ser reproporcionado)
 * e a cidade fica centrada no enquadramento da câmera. Como a malha é a mesma nos
 * dois eixos, o mapa sai QUADRADO (x e z de −97 a 97).
 *
 * A via aparada pelo prédio alto mesclado (última coluna, linhas 2-3) passa a ser a
 * HORIZONTAL z=+46, cortada em x=46 (V[3]).
 */
const G4MIN = -97; // V[0] / H[0] - meia via
const G4MAX = 97;

const GRID_4X4_ROADS = {
  width: 10,
  vertical: [
    { x: -92, zMin: G4MIN, zMax: G4MAX },
    { x: -46, zMin: G4MIN, zMax: G4MAX },
    { x: 0, zMin: G4MIN, zMax: G4MAX },
    { x: 46, zMin: G4MIN, zMax: G4MAX }, // inteira: entre boutique D3 e eletrônicos D4 (row 3) e distritos 1/2; o trecho central (z∈[0,46]) passa no meio do SHOPPING horizontal e fica escondido sob o prédio
    { x: 92, zMin: G4MIN, zMax: G4MAX }
  ],
  horizontal: [
    { z: -92, xMin: G4MIN, xMax: G4MAX },
    { z: -46, xMin: G4MIN, xMax: G4MAX },
    { z: 0, xMin: G4MIN, xMax: G4MAX },
    { z: 46, xMin: G4MIN, xMax: G4MAX }, // RESTAURADA (o shopping deixou de ser vertical em col 3; agora é horizontal em C3/C4, cortando a via x=46)
    { z: 92, xMin: G4MIN, xMax: G4MAX }
  ]
};

// GRADE 6×4 (Mexican Town): 6 colunas × 4 linhas, cada célula 46. Cada distrito é uma
// SUPERQUADRA 2×3 (2 linhas × 3 colunas). Vias dirigíveis = anel externo + cruz central
// (x=0 entre distritos esq./dir., z=0 entre cima/baixo); as internas de cada distrito viram
// calçada (markDistrictAxes). Mapa: x∈[-138,138] (±143 c/ meia via), z∈[-92,92] (±97).
// SUPERQUADRAS EMPACOTADAS: cada distrito 2×3 vira um bloco de 102×70 (3×2 células de 32 + recuo
// de perímetro de 3 em districtBed → calçada EXTERNA = calçada INTERNA = 6). Faixas de calçada dos
// canteiros vizinhos ENCOSTANDO, sem chão escuro. Linhas dirigíveis = anel (x=±112, z=±80) + cruz
// central (x=0, z=0); as internas (x=±37,±69 e z=±37) viram calçada (markDistrictAxes).
// Mapa: x∈[-112,112] (±117 c/ meia via), z∈[-80,80] (±85). land de cada distrito = 102×70.
const G6X = 117;
const G6Z = 85;
const GRID_6X4_ROADS = {
  width: 10,
  vertical: [
    { x: -112, zMin: -G6Z, zMax: G6Z },
    { x: -69, zMin: -G6Z, zMax: G6Z },
    { x: -37, zMin: -G6Z, zMax: G6Z },
    { x: 0, zMin: -G6Z, zMax: G6Z },
    { x: 37, zMin: -G6Z, zMax: G6Z },
    { x: 69, zMin: -G6Z, zMax: G6Z },
    { x: 112, zMin: -G6Z, zMax: G6Z }
  ],
  horizontal: [
    { z: -80, xMin: -G6X, xMax: G6X },
    { z: -37, xMin: -G6X, xMax: G6X },
    { z: 0, xMin: -G6X, xMax: G6X },
    { z: 37, xMin: -G6X, xMax: G6X },
    { z: 80, xMin: -G6X, xMax: G6X }
  ]
};

const FLIPPED_ROADS = flipRoadsX(BASE_ROADS);

/** Malhas fora do padrão 5×3, por `cols`×`rows` (cities.js). */
const GRIDS = { '4x4': GRID_4X4_ROADS, '6x4': GRID_6X4_ROADS };

/**
 * A via é DIRIGÍVEL? As vias que caem no EIXO INTERNO de uma superquadra viraram
 * calçada/parque (só pedestre), mas CONTINUAM na lista de `ROADS` porque é ela que
 * define a GRADE de quadras — `cityLayout` deriva V/H daqui, e tirar uma linha
 * embaralharia todos os índices de coluna/linha dos lotes. Por isso elas são apenas
 * MARCADAS: quem trata de CARRO (RoadGraph, Parking, CityAgents, GlowDots) filtra
 * por aqui; quem trata de GRADE continua usando a lista inteira.
 */
export const isDrivable = (r) => r.drivable !== false;

/**
 * A via faz parte da GRADE de quadras? Os TOCOS DE ACESSO (abaixo) são asfalto de
 * verdade, mas não são linhas da grade — quem deriva coluna/linha filtra por aqui,
 * senão os índices dos lotes se deslocariam.
 */
export const isGridLine = (r) => !r.offGrid;

/**
 * Marca como não-dirigível o eixo interno de cada distrito. Vale a premissa de que os
 * distritos LADRILHAM a grade (é o caso da Mexican Town): assim o eixo não sobra em
 * nenhum trecho fora de superquadra, e dá para marcar a via inteira em vez de fatiá-la.
 */
function markDistrictAxes(roads, city) {
  const ds = city.districts;
  if (!ds || !ds.length) return roads;
  // marca TODAS as vias INTERNAS de cada distrito (2×3 tem 2 internas em x e 1 em z)
  const axisX = new Set();
  const axisZ = new Set();
  for (const d of ds) {
    for (let c = d.c0 + 1; c <= d.c1; c++) axisX.add(roads.vertical[c].x);
    for (let r = d.r0 + 1; r <= d.r1; r++) axisZ.add(roads.horizontal[r].z);
  }
  return {
    width: roads.width,
    vertical: roads.vertical.map((r) => (axisX.has(r.x) ? { ...r, drivable: false } : r)),
    horizontal: roads.horizontal.map((r) => (axisZ.has(r.z) ? { ...r, drivable: false } : r))
  };
}

/**
 * Malha viária de UMA cidade (não necessariamente a ativa). Exportada porque a
 * prévia admin (/models) percorre os lotes de TODAS as cidades e precisa medir
 * cada lote na grade DELE — medir um lote de 5×3 na grade 4×4 daria NaN.
 *
 * Grade nova sem malha definida ABORTA na carga (em vez de cair calada no padrão
 * 5×3 e render prédio/quadra fora de lugar).
 */
/**
 * TOCOS DE ACESSO — pedaço curto de asfalto que liga uma VIA DE ACESSO ao ANEL viário
 * quando a via da grade naquela coordenada não é dirigível (virou calçadão do distrito).
 *
 * É o caso das entradas em ±46 da Mexican Town: dentro da superquadra aquele eixo é
 * parque, mas FORA dela a via de acesso chega do mundo e precisa encostar em algum
 * lugar. O toco cobre só a faixa da via de perímetro — o carro desce pela avenida,
 * pega o anel e nunca entra no calçadão. É um "T" com a marginal, como na vida real.
 *
 * Marcado `offGrid` porque NÃO é linha da grade: `cityLayout` (que deriva as colunas e
 * linhas dos lotes) e o desenho das faixas/setas filtram por `isGridLine`; quem trata de
 * carro (RoadGraph, CityAgents) usa a lista toda e enxerga o toco como asfalto.
 */
function addEntryStubs(roads, city) {
  const xs = roads.vertical.map((r) => r.x);
  const zs = roads.horizontal.map((r) => r.z);
  const half = roads.width / 2;
  const band = (c) => ({ lo: c - half, hi: c + half }); // faixa da via de perímetro
  const stubsV = [];
  const stubsH = [];

  for (const er of city.entryRoads || []) {
    const road = roads.horizontal.find((r) => r.z === er.z);
    if (!road || isDrivable(road)) continue; // já é rua: entra direto, sem toco
    for (const side of er.sides) {
      const b = band(side === 'left' ? Math.min(...xs) : Math.max(...xs));
      stubsH.push({ z: er.z, xMin: b.lo, xMax: b.hi, offGrid: true });
    }
  }
  for (const ev of city.entryRoadsV || []) {
    const road = roads.vertical.find((r) => r.x === ev.x);
    if (!road || isDrivable(road)) continue;
    for (const side of ev.sides) {
      const b = band(side === 'top' ? Math.min(...zs) : Math.max(...zs));
      stubsV.push({ x: ev.x, zMin: b.lo, zMax: b.hi, offGrid: true });
    }
  }
  if (!stubsV.length && !stubsH.length) return roads;
  return {
    width: roads.width,
    vertical: [...roads.vertical, ...stubsV],
    horizontal: [...roads.horizontal, ...stubsH]
  };
}

export function roadsFor(city) {
  const cols = city.cols ?? 5;
  const rows = city.rows ?? 3;
  if (cols !== 5 || rows !== 3) {
    const grid = GRIDS[`${cols}x${rows}`];
    if (!grid) throw new Error(`config.js: grade ${cols}×${rows} (cidade "${city.id}") não tem malha definida`);
    return addEntryStubs(markDistrictAxes(grid, city), city);
  }
  if (city.flipX) return FLIPPED_ROADS;
  if (city.tallBlockHoriz) return TALL_HORIZ_ROADS;
  return BASE_ROADS;
}

export const ROADS = roadsFor(ACTIVE_CITY);

/**
 * FATOR DE GRADE — o enquadramento (câmera + fog) foi calibrado na grade 5×3, que
 * ocupa 240×148 de chão. Numa grade MAIOR a cidade não cabe no mesmo quadro, então a
 * distância de carregamento e o fog recuam pelo maior "excesso" de extensão. Na 5×3 o
 * fator dá exatamente 1 → as três cidades originais ficam INTACTAS; a 4×4 (194×194) é
 * 31% mais funda e recua na mesma proporção.
 */
const REF_SPAN_X = 240; // 5×3: x de −120 a 120
const REF_SPAN_Z = 148; // 5×3: z de −74 a 74
const spanOf = (vals) => Math.max(...vals) - Math.min(...vals) + ROADS.width;
export const GRID_FIT = Math.max(
  1,
  spanOf(ROADS.vertical.map((r) => r.x)) / REF_SPAN_X,
  spanOf(ROADS.horizontal.map((r) => r.z)) / REF_SPAN_Z
);

/**
 * ENQUADRAMENTO DE CARREGAMENTO — cada cidade pode trazer o SEU em `camera`
 * (cities.js), medido na tela real com o medidor de performance (bloco CÂMERA).
 * Quando a cidade traz o campo, os valores são FINAIS: nada de escalar por
 * GRID_FIT (a medição já foi feita naquela grade). Hoje as QUATRO cidades trazem o
 * campo; o caminho abaixo é a rede de segurança para uma cidade nova ainda não
 * medida: enquadramento HISTÓRICO recuado ao longo do eixo de visão pelo fator da
 * grade — mesmo alvo, mesmo ângulo, só mais longe.
 */
const FALLBACK_TARGET = [-0.5, 3.8, 18.2];
const FALLBACK_POSITION = [-0.5, 167.3, 204.5];
const CAM_TARGET = ACTIVE_CITY.camera?.target ?? FALLBACK_TARGET;
const camPosition =
  ACTIVE_CITY.camera?.position ??
  (GRID_FIT === 1
    ? FALLBACK_POSITION
    : FALLBACK_TARGET.map((t, i) => t + (FALLBACK_POSITION[i] - t) * GRID_FIT));

/** Câmera (vista aérea bem de cima, levemente inclinada, como a referência). */
export const CAMERA = {
  fov: 33,
  // Posição/alvo vêm da CIDADE ATIVA (cities.js → `camera`), medidos na tela real —
  // é a vista do load E o teto de zoom-out da câmera dinâmica. Cidade sem o campo
  // cai no enquadramento histórico (vista 3/4 inclinada, destacando as fachadas)
  // recuado pelo fator da grade. Ver o bloco acima.
  position: camPosition,
  target: CAM_TARGET,
  minPolar: 0.05,
  maxPolar: 1.02, // permite o ângulo 3/4 (e orbitar um pouco mais baixo)
  minDistance: 120,
  maxDistance: 520 * GRID_FIT,
  // CÂMERA LIVRE — limites folgados usados no modo MANUAL (câmera dinâmica
  // DESLIGADA), pensados para enquadrar screenshots de alta resolução p/ impressão
  // (lona/banner): chega bem mais perto, abre mais e baixa o ângulo até quase o
  // horizonte ("hero shots" da cidade). A câmera dinâmica dirige a câmera direto e
  // IGNORA os controles, então liberar aqui não afeta o modo automático.
  free: {
    minPolar: 0.02,
    maxPolar: 1.4, // ~80° (quase horizontal)
    minDistance: 28,
    maxDistance: 640 * GRID_FIT
  }
};

/** Pós-processamento (bloom faz o verde "acender"). */
export const BLOOM = {
  strength: 0.48,
  radius: 0.55,
  threshold: 0.86
};
